import { Grid, useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import type { User } from '@sentry/nextjs';
import { useRouter } from 'next/router';
import React, { useContext, useEffect } from 'react';

import { useReviewFromUserQuery } from '@/__generated__/user-graphql';
import Button from '@/contents/General/Button';
import Chip from '@/contents/General/Chip';
import Icon from '@/contents/General/Icon';
import { GlobalContext } from '@/contents/hooks/state';
import useCampSiteLikeFromPublic from '@/modules/CampSiteLikeList/public/useCampSiteLikeFromPublic';
import useGetLoginUser from '@/modules/User/user/useGetLoginUser';
import useUserNotificationNum from '@/modules/User/useUserNotificationNum';
import theme from '@/styles/theme';
import { Page } from '@/types/domain';

const HeaderNavi = styled(Box)<{ is_pc: string }>`
  display: flex;
  align-item: center;
  position: fixed;
  padding: 0 ${theme.spacing(3)};
  width: 100%;
  height: 57px;
  border-top: solid 1px #f4f8f6;
  background: #ffffff;
  z-index: 10;
  ${(props) => !props.is_pc && `top: 64px; border-top: solid 1px #f4f8f6;`}
  ${(props) => props.is_pc && `max-width: 506px; border-radius: 40px 40px 0 0`}
`;

export const MypageMenu = () => {
  const router = useRouter();
  const { page, isEditLoading } = useContext(GlobalContext);
  const { user: loginUser, refetch, isLogin } = useGetLoginUser();
  const { data: campSiteLikeData } = useCampSiteLikeFromPublic(
    loginUser?.id ?? '',
    {}
  );
  const userId = (loginUser as User).id ?? '';
  const { totalNum } = useUserNotificationNum();
  const { data: reviewData } = useReviewFromUserQuery({});
  const isPC = useMediaQuery('(min-width:900px)');

  useEffect(() => {
    refetch();
  }, [isEditLoading]);

  if (!loginUser && typeof loginUser === 'object') return <></>;

  return (
    <HeaderNavi is_pc={isPC ? isPC.toString() : ''}>
      <Grid container>
        <Grid item xs={3}>
          <Button
            onClick={() => router.push(`/camper/${userId}`)}
            component="label"
            sx={{ width: '100%', height: '100%' }}
          >
            <Box position="relative" height={28}>
              <Icon
                iconType="mypage-account"
                color={page === Page.MYPAGE_ACCOUNT ? 'primary' : ''}
              />
              {totalNum > 0 && (
                <Chip
                  label={totalNum}
                  chipColor="red"
                  sx={{
                    position: 'absolute',
                    top: '20px',
                    left: '20px',
                    maxWidth: '48px',
                  }}
                />
              )}
            </Box>
          </Button>
        </Grid>
        <Grid item xs={3}>
          <Button
            onClick={() => router.push(`/camper/${userId}/favorite`)}
            component="label"
            sx={{ width: '100%', height: '100%' }}
          >
            <Box position="relative" height={28}>
              <Icon
                iconType="mypage-favorite"
                color={page === Page.MYPAGE_FAVORITE ? 'primary' : ''}
              />
              <Chip
                label={campSiteLikeData?.all_count.aggregate?.count ?? 0}
                chipColor="secondary"
                sx={{
                  position: 'absolute',
                  top: '20px',
                  left: '20px',
                  maxWidth: '48px',
                }}
              />
            </Box>
          </Button>
        </Grid>
        <Grid item xs={3}>
          <Button
            onClick={() => router.push(`/camper/${userId}/log`)}
            component="label"
            sx={{ width: '100%', height: '100%' }}
          >
            <Box position="relative" height={28}>
              <Icon
                iconType="mypage-review"
                color={page === Page.MYPAGE_REVIEW ? 'primary' : ''}
              />
              <Chip
                label={reviewData?.review.length ?? 0}
                color="secondary"
                chipColor="secondary"
                sx={{
                  position: 'absolute',
                  top: '20px',
                  left: '20px',
                  maxWidth: '48px',
                }}
              />
            </Box>
          </Button>
        </Grid>
        <Grid item xs={3}>
          <Button
            onClick={() => router.push(`/camper/${userId}/visited`)}
            component="label"
            sx={{ width: '100%', height: '100%' }}
          >
            <Box position="relative" height={28}>
              <Icon
                iconType="mypage-visit"
                color={page === Page.MYPAGE_VISITED ? 'primary' : ''}
              />
              <Chip
                label={!isLogin ? 0 : loginUser.total_visited_num}
                chipColor="secondary"
                sx={{
                  position: 'absolute',
                  top: '20px',
                  left: '20px',
                  maxWidth: '48px',
                }}
              />
            </Box>
          </Button>
        </Grid>
      </Grid>
    </HeaderNavi>
  );
};
